import { NextPage } from 'next'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleCheck, faCopy } from '@fortawesome/free-regular-svg-icons'
import { faArrowUpRightFromSquare, faCertificate, faPlay } from '@fortawesome/free-solid-svg-icons'
import type { TypedDataSigner } from '@ethersproject/abstract-signer'
import { BigNumber, Signer } from 'ethers'
import { useSession } from 'next-auth/react'
import {
  useAddress,
  useChainId,
  useSetIsWalletModalOpen,
  useSigner,
  useSwitchChain
} from '@thirdweb-dev/react'
import requestMint from '~/modules/Trophies/RequestMint/Application/RequestMint'
import { HtmlPageMeta } from '~/components/HtmlPageMeta/HtmlPageMeta'
import { HtmlPageMetaResourceService } from '~/components/HtmlPageMeta/HtmlPageMetaResourceService'
import { BackLink } from '~/components/BackLink'
import { ComponentTrophyDtoTransformer } from '~/modules/Products/Infrastructure/ComponentTrophyDtoTransformer'
import {
  TrophyDetailComponentTranslatorDto
} from '~/modules/Trophies/Infrastructure/TrophyDetailComponentTranslatorDto'
import { repairTextEncoding } from '~/modules/Shared/Domain/Helpers/String'
import {
  TrophyAttributeDto,
  TrophyDetailComponentDto
} from '~/modules/Trophies/Infrastructure/TrophyDetailComponentDto'
import { TrophyMemories } from '~/modules/TrophyMemories/Infrastructure/Components/TrophyMemories'
import { TrophyDetailPageViewModel } from '~/modules/PresentationAdapters/TrophyDetailPageAdapter'
import { getBrowserAnalyticsService } from '~/modules/Analytics/Infrastructure/safeAnalytics'
import { ComponentTrophyDto } from '~/modules/Products/Infrastructure/Dtos/ComponentTrophyDto'
import { toInternalMuseumHref } from '~/modules/Gallery/url'
import styles from './MarketTrophyDetailPage.module.scss'

export interface MarketTrophyDetailPageProps {
  viewModel: TrophyDetailPageViewModel
}

interface MediaItem {
  key: string
  kind: 'image' | 'video'
  src: string
  poster?: string
  thumbnailSrc: string
  showPlayIcon?: boolean
}

const hiddenAttributeTypes = new Set([
  'Award Type',
  'Competition',
  'Full Name',
  'Organizer',
])

export const MarketTrophyDetailPage: NextPage<MarketTrophyDetailPageProps> = ({ viewModel }) => {
  const { t } = useTranslation('trophy_details')
  const router = useRouter()
  const session = useSession()
  const address = useAddress()
  const signer = useSigner()
  const chainId = useChainId()
  const switchChain = useSwitchChain()
  const setIsWalletModalOpen = useSetIsWalletModalOpen()
  const [activeMediaIndex, setActiveMediaIndex] = useState(0)
  const [mintedLocally, setMintedLocally] = useState(false)
  const [isMinting, setIsMinting] = useState(false)
  const [mintError, setMintError] = useState(false)
  const [shouldRenderMemories, setShouldRenderMemories] = useState(false)
  const memoriesAnchorRef = useRef<HTMLElement | null>(null)
  const trophy = TrophyDetailComponentTranslatorDto.fromApplication(viewModel.trophy)
  const organizerLogo = trophy.organizer.logoUrl ?? trophy.organizer.avatarUrl
  const detailAttributes = trophy.attributes.filter((attribute) => !hiddenAttributeTypes.has(attribute.type))
  const relatedTrophies = useMemo(
    () => viewModel.relatedTrophies
      .slice(0, 4)
      .map((relatedTrophy) => ComponentTrophyDtoTransformer.fromApplication(relatedTrophy)),
    [viewModel.relatedTrophies]
  )
  const mediaItems = useMemo(() => buildMediaItems(trophy), [trophy])
  const activeMedia = mediaItems[activeMediaIndex] ?? mediaItems[0]
  const previousMedia = mediaItems[(activeMediaIndex + mediaItems.length - 1) % mediaItems.length]
  const nextMedia = mediaItems[(activeMediaIndex + 1) % mediaItems.length]
  const trophyTitle = trophy.title || trophy.name
  const trophySubtitle =
    findAttributeValue(trophy.attributes, 'Award Type') ??
    trophy.subtitle
  const ownerName = getOwnerName(viewModel, trophy.attributes)
  const ownerGalleryUrl = viewModel.ownerProfile?.museumUrl
    ? toInternalMuseumHref(viewModel.ownerProfile.museumUrl)
    : null
  const verifyUrl = makeVerifyUrl(trophy)
  const currentViewerOwnsTrophy =
    viewModel.owner ||
    (
      session.status === 'authenticated' &&
      session.data.user.id === trophy.userId
    )
  const isOwnedState = currentViewerOwnsTrophy && (trophy.mintStatus === 'minted' || mintedLocally)
  const canMint =
    !isOwnedState &&
    trophy.mintStatus === 'pending' &&
    currentViewerOwnsTrophy
  const isWalletConnected = address !== undefined && signer !== undefined && chainId !== undefined
  const htmlPageMetaUrlProps = (
    new HtmlPageMetaResourceService(viewModel.trophy.name, viewModel.trophy.description, viewModel.trophy.image)
  ).getProperties()
  const htmlPageMetaProps = { ...viewModel.htmlPageMetaContextProps, ...htmlPageMetaUrlProps }

  useEffect(() => {
    setActiveMediaIndex(0)
    setMintedLocally(false)
    setIsMinting(false)
    setMintError(false)
  }, [trophy.id])

  useEffect(() => {
    async function sendProductViewedEvent () {
      const analytics = getBrowserAnalyticsService()

      if (analytics === null) {
        return
      }

      await analytics.trackProductViewed({
        category: 'trophy',
        collection: trophy.competition,
        organizer: trophy.organizer.name,
        productId: trophy.id,
        language: viewModel.language,
      })
    }

    sendProductViewedEvent().catch(() => {
      console.error(`Failed to send Product viewed event for trophy with ID ${trophy.id}`)
    })
  }, [
    trophy.id,
    trophy.competition,
    trophy.organizer.name,
    viewModel.language,
  ])

  useEffect(() => {
    if (trophy.memories.length > 0) {
      setShouldRenderMemories(true)

      return
    }

    if (
      trophy.memories.length === 0 &&
      !viewModel.owner
    ) {
      return
    }

    if (typeof window === 'undefined') {
      setShouldRenderMemories(true)

      return
    }

    if (typeof IntersectionObserver === 'undefined') {
      setShouldRenderMemories(true)

      return
    }

    const anchor = memoriesAnchorRef.current

    if (anchor === null) {
      setShouldRenderMemories(true)

      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShouldRenderMemories(true)
          observer.disconnect()
        }
      },
      {
        rootMargin: '240px 0px',
      }
    )

    observer.observe(anchor)

    return () => observer.disconnect()
  }, [trophy.memories.length, viewModel.owner])

  let memories: ReactElement | null = null

  if (shouldRenderMemories && (trophy.memories.length > 0 || viewModel.owner)) {
    memories = (
      <TrophyMemories
        trophy={ trophy }
        isOwner={ viewModel.owner }
        appearance='market'
      />
    )
  }

  const handleMint = async () => {
    if (!canMint) {
      return
    }

    setMintError(false)

    if (!isWalletConnected) {
      setIsWalletModalOpen(true)

      return
    }

    const targetChainId = trophy.collection?.contractChain
      ? parseInt(trophy.collection.contractChain)
      : undefined

    if (!targetChainId) {
      return
    }

    if (targetChainId !== chainId) {
      switchChain(targetChainId)

      return
    }

    setIsMinting(true)

    try {
      await requestMint(
        signer as Signer & TypedDataSigner,
        targetChainId,
        BigNumber.from(trophy.id),
        address
      )
      setMintedLocally(true)
    } catch (exception: unknown) {
      setMintError(true)
      console.error(exception)
    } finally {
      setIsMinting(false)
    }
  }

  const handleCopyUrl = async () => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return
    }

    try {
      await navigator.clipboard.writeText(window.location.href)
    } catch {
      console.error(`Failed to copy trophy URL for trophy with ID ${trophy.id}`)
    }
  }

  return (
    <>
      <HtmlPageMeta { ...htmlPageMetaProps } />

      <div className={ styles.marketTrophyDetailPage }>
        <main className={ styles.marketTrophyDetailPage__content }>
          { viewModel.backlink && (
            <div className={ styles.marketTrophyDetailPage__backLink }>
              <BackLink />
            </div>
          ) }

          <section className={ styles.marketTrophyDetailPage__layout }>
            <div className={ styles.marketTrophyDetailPage__actions }>
              { verifyUrl && (
                <ActionPill
                  href={ verifyUrl }
                  icon={ faCertificate }
                  longLabel={ t('verify_button_title_long') }
                  shortLabel={ t('verify_button_title_short') }
                />
              ) }

              <ActionPill
                icon={ faCopy }
                longLabel={ t('copy_button_title') }
                shortLabel={ t('copy_button_title') }
                onClick={ handleCopyUrl }
              />
            </div>

            <section className={ styles.marketTrophyDetailPage__mediaSection }>
              <div className={ styles.marketTrophyDetailPage__mediaDesktop }>
                <div className={ styles.marketTrophyDetailPage__mediaMain }>
                  <MediaAsset
                    item={ activeMedia }
                    alt={ t('trophy_details_image_alt') }
                    className={ styles.marketTrophyDetailPage__mediaAsset }
                  />
                </div>

                <div className={ styles.marketTrophyDetailPage__thumbRow }>
                  { mediaItems.slice(1, 3).map((item, index) => {
                    const itemIndex = index + 1

                    return (
                      <button
                        key={ item.key }
                        type="button"
                        className={ styles.marketTrophyDetailPage__thumbButton }
                        onClick={ () => setActiveMediaIndex(itemIndex) }
                        aria-label={ `${trophyTitle} media ${itemIndex + 1}` }
                      >
                        <img
                          src={ item.thumbnailSrc }
                          alt=""
                          className={ styles.marketTrophyDetailPage__thumbImage }
                          width={ 80 }
                          height={ 80 }
                          loading="lazy"
                        />

                        { item.showPlayIcon && (
                          <div className={ styles.marketTrophyDetailPage__thumbPlay }>
                            <FontAwesomeIcon icon={ faPlay } />
                          </div>
                        ) }
                      </button>
                    )
                  }) }
                </div>
              </div>

              <div className={ styles.marketTrophyDetailPage__mediaMobile }>
                <div className={ styles.marketTrophyDetailPage__mobileCarousel }>
                  { mediaItems.length > 1 && (
                    <>
                      <div className={ styles.marketTrophyDetailPage__mobilePeekLeft }>
                        <img
                          src={ previousMedia.thumbnailSrc }
                          alt=""
                          className={ styles.marketTrophyDetailPage__mobilePeekImage }
                          width={ 240 }
                          height={ 240 }
                          loading="lazy"
                        />
                        { previousMedia.showPlayIcon && (
                          <div className={ styles.marketTrophyDetailPage__mobilePeekPlay }>
                            <FontAwesomeIcon icon={ faPlay } />
                          </div>
                        ) }
                      </div>

                      <div className={ styles.marketTrophyDetailPage__mobilePeekRight }>
                        <img
                          src={ nextMedia.thumbnailSrc }
                          alt=""
                          className={ styles.marketTrophyDetailPage__mobilePeekImage }
                          width={ 240 }
                          height={ 240 }
                          loading="lazy"
                        />
                        { nextMedia.showPlayIcon && (
                          <div className={ styles.marketTrophyDetailPage__mobilePeekPlay }>
                            <FontAwesomeIcon icon={ faPlay } />
                          </div>
                        ) }
                      </div>
                    </>
                  ) }

                  <div className={ styles.marketTrophyDetailPage__mobileMain }>
                    <MediaAsset
                      item={ activeMedia }
                      alt={ t('trophy_details_image_alt') }
                      className={ styles.marketTrophyDetailPage__mediaAsset }
                    />
                  </div>
                </div>

                { mediaItems.length > 1 && (
                  <div className={ styles.marketTrophyDetailPage__pagination }>
                    { mediaItems.map((item, index) => (
                      <button
                        key={ item.key }
                        type="button"
                        className={ `
                          ${styles.marketTrophyDetailPage__paginationDot}
                          ${index === activeMediaIndex ? styles.marketTrophyDetailPage__paginationDot__active : ''}
                        ` }
                        onClick={ () => setActiveMediaIndex(index) }
                        aria-label={ `${trophyTitle} media ${index + 1}` }
                      />
                    )) }
                  </div>
                ) }
              </div>
            </section>

            <Link
              href={ `/organizers/${trophy.organizer.id}` }
              className={ styles.marketTrophyDetailPage__organizerChip }
              passHref
            >
              <div className={ styles.marketTrophyDetailPage__organizerIdentity }>
                <div className={ styles.marketTrophyDetailPage__organizerAvatar }>
                  { organizerLogo
                    ? (
                        <img
                          src={ organizerLogo }
                          alt={ trophy.organizer.name }
                          className={ styles.marketTrophyDetailPage__organizerAvatarImage }
                          width={ 32 }
                          height={ 32 }
                          loading="lazy"
                        />
                      )
                    : (
                        <span className={ styles.marketTrophyDetailPage__organizerAvatarFallback }>
                          { trophy.organizer.name.charAt(0) }
                        </span>
                      ) }
                </div>

                <div className={ styles.marketTrophyDetailPage__organizerText }>
                  <span className={ styles.marketTrophyDetailPage__organizerName }>
                    { trophy.organizer.name }
                  </span>

                  <FontAwesomeIcon
                    icon={ faCircleCheck }
                    className={ styles.marketTrophyDetailPage__organizerCheck }
                  />
                </div>
              </div>

              <FontAwesomeIcon
                icon={ faArrowUpRightFromSquare }
                className={ styles.marketTrophyDetailPage__chipArrow }
              />
            </Link>

            <section className={ styles.marketTrophyDetailPage__titleCard }>
              <div>
                <h1 className={ styles.marketTrophyDetailPage__title }>
                  { trophyTitle }
                </h1>

                <p className={ styles.marketTrophyDetailPage__subtitle }>
                  { trophySubtitle }
                </p>
              </div>

              <div className={ styles.marketTrophyDetailPage__ownerBlock }>
                <p className={ styles.marketTrophyDetailPage__ownerLabel }>
                  { t('owner_label') }
                </p>

                { ownerGalleryUrl
                  ? (
                      <Link
                        href={ ownerGalleryUrl }
                        locale={ router.locale }
                        className={ styles.marketTrophyDetailPage__ownerLink }
                      >
                        <span>{ ownerName }</span>
                        <FontAwesomeIcon icon={ faArrowUpRightFromSquare } />
                      </Link>
                    )
                  : (
                      <div className={ styles.marketTrophyDetailPage__ownerName }>
                        <span>{ ownerName }</span>
                      </div>
                    ) }
              </div>
            </section>

            <StatusCard
              t={ t }
              isOwnedState={ isOwnedState }
              currentViewerOwnsTrophy={ currentViewerOwnsTrophy }
              canMint={ canMint }
              isMinting={ isMinting }
              mintError={ mintError }
              onMint={ handleMint }
            />

            <section className={ styles.marketTrophyDetailPage__detailsTable }>
              <div className={ styles.marketTrophyDetailPage__detailsGrid }>
                { detailAttributes.map((attribute) => (
                  <div
                    key={ `${attribute.type}-${attribute.value}` }
                    className={ styles.marketTrophyDetailPage__detailCell }
                  >
                    <p className={ styles.marketTrophyDetailPage__detailLabel }>
                      { attribute.type }
                    </p>
                    <p className={ styles.marketTrophyDetailPage__detailValue }>
                      { attribute.value }
                    </p>
                  </div>
                )) }
              </div>
            </section>

            <section className={ styles.marketTrophyDetailPage__descriptionCard }>
              <p className={ styles.marketTrophyDetailPage__descriptionLabel }>
                { t('description_title') }
              </p>
              <p className={ styles.marketTrophyDetailPage__descriptionText }>
                { trophy.description }
              </p>
            </section>
          </section>

          { (trophy.memories.length > 0 || viewModel.owner) && (
            <section
              ref={ memoriesAnchorRef }
              className={ styles.marketTrophyDetailPage__secondarySection }
            >
              { memories ?? (
                <div className={ styles.marketTrophyDetailPage__memoriesPlaceholder } />
              ) }
            </section>
          ) }

          { relatedTrophies.length > 0 && (
            <section className={ styles.marketTrophyDetailPage__secondarySection }>
              <div className={ styles.marketTrophyDetailPage__relatedHeader }>
                <div className={ styles.marketTrophyDetailPage__relatedTitleGroup }>
                  <h2 className={ styles.marketTrophyDetailPage__relatedTitle }>
                    { t('related_title') }
                  </h2>
                  <span className={ styles.marketTrophyDetailPage__relatedCount }>
                    { t('related_items_count', { count: relatedTrophies.length }) }
                  </span>
                </div>

                <Link
                  href={ `/trophies?organizerId=${trophy.organizer.id}` }
                  className={ styles.marketTrophyDetailPage__relatedMore }
                  passHref
                >
                  <span>{ t('related_more') }</span>
                  <FontAwesomeIcon icon={ faArrowUpRightFromSquare } />
                </Link>
              </div>

              <div className={ styles.marketTrophyDetailPage__relatedGrid }>
                { relatedTrophies.map((relatedTrophy) => (
                  <RelatedCard
                    key={ relatedTrophy.id }
                    trophy={ relatedTrophy }
                  />
                )) }
              </div>
            </section>
          ) }
        </main>
      </div>
    </>
  )
}

interface ActionPillProps {
  href?: string | null
  icon: typeof faCopy
  longLabel: string
  shortLabel: string
  onClick?: () => void
}

const ActionPill = ({ href, icon, longLabel, shortLabel, onClick }: ActionPillProps) => {
  const content = (
    <>
      <FontAwesomeIcon
        icon={ icon }
        className={ styles.marketTrophyDetailPage__actionIcon }
        aria-hidden={ true }
      />
      <span className={ styles.marketTrophyDetailPage__actionLabel__desktop }>
        { longLabel }
      </span>
      <span className={ styles.marketTrophyDetailPage__actionLabel__mobile }>
        { shortLabel }
      </span>
    </>
  )

  if (href) {
    return (
      <a
        href={ href }
        target="_blank"
        rel="noreferrer"
        className={ styles.marketTrophyDetailPage__actionPill }
      >
        { content }
      </a>
    )
  }

  return (
    <button
      type="button"
      className={ styles.marketTrophyDetailPage__actionPill }
      onClick={ onClick }
    >
      { content }
    </button>
  )
}

interface StatusCardProps {
  t: (key: string) => string
  isOwnedState: boolean
  currentViewerOwnsTrophy: boolean
  canMint: boolean
  isMinting: boolean
  mintError: boolean
  onMint: () => void
}

interface StatusCardState {
  interactive: boolean
  showArrow: boolean
  titleKey: string
  subtitleKey: string
  variant: 'success' | 'public' | 'info'
}

export function getStatusCardState ({
  isOwnedState,
  currentViewerOwnsTrophy,
  mintError,
  isMinting,
}: Pick<StatusCardProps, 'isOwnedState' | 'currentViewerOwnsTrophy' | 'mintError' | 'isMinting'>): StatusCardState {
  if (isOwnedState) {
    return {
      interactive: false,
      showArrow: false,
      titleKey: 'owner_success_title',
      subtitleKey: 'owner_success_subtitle',
      variant: 'success',
    }
  }

  if (!currentViewerOwnsTrophy) {
    return {
      interactive: false,
      showArrow: false,
      titleKey: 'public_info_title',
      subtitleKey: 'public_info_subtitle',
      variant: 'info',
    }
  }

  if (mintError) {
    return {
      interactive: true,
      showArrow: true,
      titleKey: 'owner_error_title',
      subtitleKey: 'owner_error_subtitle',
      variant: 'public',
    }
  }

  if (isMinting) {
    return {
      interactive: true,
      showArrow: true,
      titleKey: 'mint_title',
      subtitleKey: 'owner_minting_subtitle',
      variant: 'public',
    }
  }

  return {
    interactive: true,
    showArrow: true,
    titleKey: 'mint_title',
    subtitleKey: 'mint_subtitle',
    variant: 'public',
  }
}

const StatusCard = ({
  t,
  isOwnedState,
  currentViewerOwnsTrophy,
  canMint,
  isMinting,
  mintError,
  onMint,
}: StatusCardProps) => {
  const state = getStatusCardState({
    isOwnedState,
    currentViewerOwnsTrophy,
    mintError,
    isMinting,
  })

  if (state.variant === 'success') {
    return (
      <section className={ styles.marketTrophyDetailPage__statusCard__success }>
        <div className={ styles.marketTrophyDetailPage__statusIcon }>
          <FontAwesomeIcon icon={ faCircleCheck } />
        </div>

        <div>
          <p className={ styles.marketTrophyDetailPage__statusTitle }>
            { t(state.titleKey) }
          </p>
          <p className={ styles.marketTrophyDetailPage__statusSubtitle }>
            { t(state.subtitleKey) }
          </p>
        </div>
      </section>
    )
  }

  const Wrapper = state.interactive ? 'button' : 'section'

  return (
    <Wrapper
      { ...(state.interactive
        ? {
            type: 'button',
            onClick: onMint,
            disabled: !canMint || isMinting,
          }
        : {}) }
      className={ styles.marketTrophyDetailPage__statusCard__public }
    >
      <div className={ styles.marketTrophyDetailPage__statusCopy }>
        <p className={ styles.marketTrophyDetailPage__statusTitle__dark }>
          { t(state.titleKey) }
        </p>
        <p className={ styles.marketTrophyDetailPage__statusSubtitle__dark }>
          { t(state.subtitleKey) }
        </p>
      </div>

      { state.showArrow && (
        <FontAwesomeIcon
          icon={ faArrowUpRightFromSquare }
          className={ styles.marketTrophyDetailPage__statusArrow }
          aria-hidden={ true }
        />
      ) }
    </Wrapper>
  )
}

interface MediaAssetProps {
  item: MediaItem
  alt: string
  className: string
}

const MediaAsset = ({ item, alt, className }: MediaAssetProps) => {
  if (item.kind === 'video') {
    return (
      <video
        className={ className }
        poster={ item.poster }
        loop={ true }
        autoPlay={ true }
        playsInline={ true }
        muted={ true }
      >
        <source src={ item.src } />
      </video>
    )
  }

  return (
    <img
      src={ item.src }
      alt={ alt }
      className={ className }
      width={ 644 }
      height={ 644 }
    />
  )
}

interface RelatedCardProps {
  trophy: ComponentTrophyDto
}

const RelatedCard = ({ trophy }: RelatedCardProps) => {
  return (
    <Link
      href={ `/trophies/${trophy.id}` }
      className={ styles.marketTrophyDetailPage__relatedCard }
      passHref
    >
      <div className={ styles.marketTrophyDetailPage__relatedMedia }>
        <img
          src={ trophy.imageUrl }
          alt={ trophy.content.title }
          className={ styles.marketTrophyDetailPage__relatedMediaAsset }
          width={ 640 }
          height={ 640 }
          loading="lazy"
        />

        { trophy.animation && (
          <div className={ styles.marketTrophyDetailPage__relatedMediaPlay }>
            <FontAwesomeIcon icon={ faPlay } />
          </div>
        ) }
      </div>

      <div className={ styles.marketTrophyDetailPage__relatedCardCopy }>
        <p className={ styles.marketTrophyDetailPage__relatedCardTitle }>
          { trophy.content.title }
        </p>
        <p className={ styles.marketTrophyDetailPage__relatedCardHeader }>
          { trophy.content.header }
        </p>
        <p className={ styles.marketTrophyDetailPage__relatedCardSubtitle }>
          { trophy.content.subtitle }
        </p>
      </div>
    </Link>
  )
}

function buildMediaItems (trophy: TrophyDetailComponentDto): MediaItem[] {
  const items: MediaItem[] = [
    {
      key: 'primary-image',
      kind: 'image',
      src: trophy.rawImageUrl,
      thumbnailSrc: trophy.rawImageUrl,
    },
  ]

  if (trophy.animation) {
    items.push({
      key: 'animation',
      kind: 'video',
      src: trophy.animation.url,
      poster: trophy.rawImageUrl,
      thumbnailSrc: trophy.rawImageUrl,
      showPlayIcon: true,
    })
  }

  return items
}

function getOwnerName (
  viewModel: TrophyDetailPageViewModel,
  attributes: TrophyAttributeDto[]
): string {
  return repairTextEncoding(
    viewModel.ownerProfile?.name ??
    findAttributeValue(attributes, 'Full Name') ??
    findAttributeValue(attributes, 'Owner') ??
    findAttributeValue(attributes, 'Propietario') ??
    findAttributeValue(attributes, 'Athlete') ??
    '-'
  )
}

function findAttributeValue (attributes: TrophyAttributeDto[], type: string): string | null {
  return attributes.find((attribute) => attribute.type === type)?.value ?? null
}

export function makeVerifyUrl (trophy: TrophyDetailComponentDto): string | null {
  if (
    trophy.mintStatus !== 'minted' ||
    !trophy.collection ||
    !trophy.tokenId
  ) {
    return null
  }

  return `https://verify.belegends.com/check?verificationString=${
    trophy.collection.contractChain
  }/${
    trophy.collection.contractAddress
  }/${
    trophy.tokenId
  }`
}
